import * as _ from "lodash";
import * as crypto from "crypto-js";
import { LOCAL_STORAGE_KEYS } from "../consts/constants";

const decryptValue = (val: any, secretKey: string) => {
  let decryptObj = crypto.AES.decrypt(val, secretKey);
  return _.attempt(_.invoke, decryptObj, "toString", crypto.enc.Utf8);
};

export const setItem = (key: string, value: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Error setting localStorage item:', error);
  }
};

export const getItem = (key: string): any => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Error getting localStorage item:', error);
    return null;
  }
};

export const removeItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing localStorage item:', error);
  }
};

export const clear = (): void => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};


// Test flow specific localStorage functions
export const setTestData = (testId: string, data: any): void => {
  try {
    const key = `test_${testId}`;
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error setting test data:', error);
  }
};

export const getTestData = (testId: string): any => {
  try {
    const key = `test_${testId}`;
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Error getting test data:', error);
    return null;
  }
};

export const removeTestData = (testId: string): void => {
  try {
    const key = `test_${testId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing test data:', error);
  }
};

export const setTestProgress = (testId: string, progress: any): void => {
  try {
    const key = `test_progress_${testId}`;
    localStorage.setItem(key, JSON.stringify(progress));
  } catch (error) {
    console.error('Error setting test progress:', error);
  }
};

export const removeAll = () => {
  const onboardingValue = localStorage.getItem(LOCAL_STORAGE_KEYS.IS_ONBOARDING);
  localStorage.clear();
  if (onboardingValue) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.IS_ONBOARDING, onboardingValue);
  }
};

export const getTestProgress = (testId: string): any => {
  try {
    const key = `test_progress_${testId}`;
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Error getting test progress:', error);
    return null;
  }
};

export const clearAllTestData = (): void => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('test_') || key.startsWith('test_progress_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing test data:', error);
  }
};

export const setTestSession = (sessionData: any): void => {
  try {
    localStorage.setItem('test_session', JSON.stringify(sessionData));
  } catch (error) {
    console.error('Error setting test session:', error);
  }
};

export const getTestSession = (): any => {
  try {
    const item = localStorage.getItem('test_session');
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Error getting test session:', error);
    return null;
  }
};

export const clearTestSession = (): void => {
  try {
    localStorage.removeItem('test_session');
  } catch (error) {
    console.error('Error clearing test session:', error);
  }
};

// Encrypted localStorage functions (similar to sparkl-jee-neet)
export const setEncryptedItem = (key: string, val: any): void => {
  try {
    let parsedVal;

    // Check whether value is an object
    if (_.isObject(val) || _.isArray(val)) {
      parsedVal = _.attempt(JSON.stringify.bind(null, val));
    }
    // If value is string
    else if (_.isString(val)) {
      parsedVal = val;
    }

    // Encrypt key and store in localStorage
    if (!_.isError(parsedVal) && !_.isUndefined(parsedVal)) {
      parsedVal = crypto.AES.encrypt(parsedVal, key);
      localStorage.setItem(key, parsedVal.toString());
    }
  } catch (error) {
    console.error('Error setting encrypted localStorage item:', error);
  }
};

export const getEncryptedItem = (key: string): any => {
  try {
    let retrievedVal = localStorage.getItem(key);

    if (_.isNull(retrievedVal)) {
      return null;
    }
    let decryptedVal = decryptValue(retrievedVal, key);

    // value modified by user
    if (_.isError(decryptedVal) || _.isEmpty(decryptedVal)) {
      removeItem(key);
      return null;
    }

    let decryptedObj = _.attempt(JSON.parse.bind(null, decryptedVal));
    return _.isError(decryptedObj) ? decryptedVal : decryptedObj;
  } catch (error) {
    console.error('Error getting encrypted localStorage item:', error);
    return null;
  }
};

export const setDescryptedVal = (key: string, val: any): void => {
  setEncryptedItem(key, val);
};

export const getDescryptedVal = (key: string): any => {
  return getEncryptedItem(key);
};